import React from 'react';

import {

    CampoDato,
    LabelDato,
    ValorDato,

    InputDato,
    SelectDato,

    AccionesCampo,
    BotonEditarCampo,
    BotonConfirmarCampo,
    BotonCancelarCampo,

    TextoErrorDato

} from '../../Css/StyledDatosPaciente';


/*
========================================
CAMPO EDITABLE PACIENTE
========================================

Este componente concentra la lógica visual
que antes estaba repetida muchas veces
dentro de DatosPaciente.js.

Puede mostrar:

- texto
- email
- teléfono
- fecha
- número
- select

Tiene dos modos:

LECTURA
    Label + valor + botón editar.

EDICIÓN
    Label + input/select +
    cancelar + confirmar.

La lógica de guardado permanece en
DatosPaciente.js.

De esa forma este componente no conoce
Redux, Axios ni reglas de negocio.
========================================
*/

const CampoEditablePaciente = ({

    nombre,
    label,

    /*
    Valor confirmado que viene del paciente
    cargado desde Redux.
    */

    valor,


    /*
    Valor temporal mientras se está
    editando este campo.
    */

    valorEdicion,


    /*
    Tipos compatibles:
    text, email, tel, date, number
    */

    tipo = 'text',


    /*
    Si opciones tiene elementos,
    renderizamos un select en lugar
    de un input.
    */

    opciones = [],


    /*
    Determina si este campo puede ser
    modificado según las reglas actuales
    del paciente.

    Ejemplo:
    un paciente definitivo tiene campos
    que ya no pueden editarse desde esta
    ficha administrativa.
    */

    editable = true,


    /*
    Solamente un campo estará en edición
    a la vez. Este valor lo controla
    DatosPaciente.
    */

    editando = false,


    /*
    Estado mientras backend está
    procesando el cambio.
    */

    guardando = false,


    /*
    Se utilizará después para destacar
    visualmente información importante
    para completar/habilitar al paciente.

    No significa que el campo tenga error.
    */

    destacado = false,
    faltante = false,


    /*
    Permite que un campo ocupe el ancho
    completo de la ficha.
    */

    completo = false,


    /*
    Error específico del campo.
    */

    error = '',


    /*
    Texto mostrado cuando el dato todavía
    no fue cargado.
    */

    textoVacio = 'Sin información',


    /*
    Eventos controlados por el padre.
    */

    onEditar,
    onCambiar,
    onCancelar,
    onGuardar

}) => {


    /*
    ====================================
    CAMBIO DEL CONTROL
    ====================================
    */

    const manejarCambio = (
        e
    ) => {

        if (
            onCambiar
        ) {

            onCambiar(
                e.target.value
            );

        }

    };


    /*
    ====================================
    TECLADO
    ====================================

    Enter:
        guarda un input común.

    Escape:
        cancela la edición.

    En selects también permitimos Escape.

    En el futuro, si agregamos textarea,
    Enter podrá tener otro comportamiento.
    */

    const manejarTecla = (
        e
    ) => {

        if (
            e.key === 'Escape'
        ) {

            e.preventDefault();


            if (
                !guardando &&
                onCancelar
            ) {

                onCancelar();

            }


            return;

        }


        if (
            e.key === 'Enter' &&
            opciones.length === 0
        ) {

            e.preventDefault();


            if (
                !guardando &&
                onGuardar
            ) {

                onGuardar();

            }

        }

    };


    /*
    ====================================
    VALOR PARA MOSTRAR
    ====================================
    */

    const valorEstaVacio =

        valor === null ||

        valor === undefined ||

        String(
            valor
        ).trim() === '';


    /*
    ====================================
    RENDER
    ====================================
    */

    return (

        <CampoDato

            data-completo={
                completo
                    ?
                    'true'
                    :
                    'false'
            }

            data-destacado={
                destacado
                    ?
                    'true'
                    :
                    'false'
            }

            data-faltante={
                faltante
                    ?
                    'true'
                    :
                    'false'
            }

            data-error={
                error
                    ?
                    'true'
                    :
                    'false'
            }

        >


            <LabelDato
                htmlFor={
                    editando
                        ?
                        `editar-${nombre}`
                        :
                        undefined
                }
            >

                {label}

            </LabelDato>


            {
                !editando
                    ?


                    /*
                    ====================================
                    MODO LECTURA
                    ====================================
                    */

                    <ValorDato

                        $vacio={
                            valorEstaVacio
                        }

                    >

                        {
                            valorEstaVacio
                                ?
                                textoVacio
                                :
                                valor
                        }

                    </ValorDato>


                    :


                    /*
                    ====================================
                    MODO EDICIÓN
                    ====================================
                    */

                    (
                        opciones.length > 0
                            ?

                            <SelectDato

                                id={
                                    `editar-${nombre}`
                                }

                                name={
                                    nombre
                                }

                                value={
                                    valorEdicion ?? ''
                                }

                                disabled={
                                    guardando
                                }

                                autoFocus

                                onChange={
                                    manejarCambio
                                }

                                onKeyDown={
                                    manejarTecla
                                }

                            >

                                <option value="">
                                    Seleccionar
                                </option>


                                {
                                    opciones.map(
                                        opcion => (

                                            <option

                                                key={
                                                    opcion.valor
                                                }

                                                value={
                                                    opcion.valor
                                                }

                                            >

                                                {
                                                    opcion.texto
                                                }

                                            </option>

                                        )
                                    )
                                }

                            </SelectDato>

                            :

                            <InputDato

                                id={
                                    `editar-${nombre}`
                                }

                                name={
                                    nombre
                                }

                                type={
                                    tipo
                                }

                                value={
                                    valorEdicion ?? ''
                                }

                                disabled={
                                    guardando
                                }

                                autoFocus

                                onChange={
                                    manejarCambio
                                }

                                onKeyDown={
                                    manejarTecla
                                }

                            />
                    )
            }


            <AccionesCampo>


                {
                    !editando
                        ?


                        /*
                        ====================================
                        EDITAR
                        ====================================
                        */

                        editable && (

                            <BotonEditarCampo

                                type="button"

                                title={
                                    `Editar ${label}`
                                }

                                aria-label={
                                    `Editar ${label}`
                                }

                                onClick={
                                    onEditar
                                }

                            >

                                ✎

                            </BotonEditarCampo>

                        )


                        :


                        /*
                        ====================================
                        CANCELAR / GUARDAR
                        ====================================
                        */

                        <>

                            <BotonCancelarCampo

                                type="button"

                                title="Cancelar"

                                aria-label={
                                    `Cancelar edición de ${label}`
                                }

                                disabled={
                                    guardando
                                }

                                onClick={
                                    onCancelar
                                }

                            >

                                ×

                            </BotonCancelarCampo>


                            <BotonConfirmarCampo

                                type="button"

                                title="Guardar"

                                aria-label={
                                    `Guardar ${label}`
                                }

                                disabled={
                                    guardando
                                }

                                onClick={
                                    onGuardar
                                }

                            >

                                {
                                    guardando
                                        ?
                                        '…'
                                        :
                                        '✓'
                                }

                            </BotonConfirmarCampo>

                        </>

                }

            </AccionesCampo>


            {
                error && (

                    <TextoErrorDato>

                        {error}

                    </TextoErrorDato>

                )
            }


        </CampoDato>

    );

};


export default CampoEditablePaciente;